import { FileText, ClipboardCheck, Loader2 } from 'lucide-react';
import { FeedbackDisplay } from './FeedbackDisplay';
import type { TranscriptEntry } from './GeminiLiveWidget';

export type TabType = 'details' | 'results';

interface SessionDetailsViewProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  analysis: any;
  transcript: TranscriptEntry[];
  scenario: {
    id: number;
    text: string;
    scientificScenario?: string;
  };
  duration?: number;
  analyzing?: boolean;
  hideTabs?: boolean;
}

export function SessionDetailsView({
  activeTab,
  setActiveTab,
  analysis,
  transcript,
  scenario,
  duration,
  analyzing = false,
  hideTabs = false,
}: SessionDetailsViewProps) {
  // Convert markdown to HTML
  const markdownToHtml = (markdown: string): string => {
    if (!markdown) return '';
    
    let html = markdown;
    
    // Convert **text** to <strong>text</strong>
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Split into lines for processing
    const lines = html.split('\n');
    const result: string[] = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Empty line
      if (!line) {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push('<br/>');
        continue;
      }
      
      // List item
      if (line.startsWith('- ')) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${line.substring(2)}</li>`);
      } else {
        // Regular line
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(`<p>${line}</p>`);
      }
    }
    
    // Close list if still open
    if (inList) {
      result.push('</ul>');
    }
    
    return result.join('\n');
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Tab Navigation - Only shown if not hidden by parent */}
      {!hideTabs && (
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0b0d12]/60 pb-4 pt-4 border-b border-white/5">
          <div className="flex p-1 bg-white/5 rounded-xl border border-white/10">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'details' 
                  ? 'bg-white/10 text-white shadow-sm' 
                  : 'text-muted hover:text-white'
              }`}
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span className="truncate">Case Details</span>
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm relative ${
                activeTab === 'results' 
                  ? 'bg-white/10 text-white shadow-sm' 
                  : 'text-muted hover:text-white'
              }`}
            >
              <ClipboardCheck className="w-4 h-4 shrink-0" />
              <span className="truncate">Results</span>
              
              {analyzing && (
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
              )}
              
            {!analyzing && analysis && (
              <div className="absolute -top-1 -right-0.5 px-1 py-0.5 rounded-[4px] text-[7px] font-black tracking-tighter uppercase shadow-sm border border-[#10b981]/20" style={{
                background: '#10b981',
                color: '#fff',
                lineHeight: '1',
              }}>
                Analyzed
              </div>
            )}
            </button>
          </div>
        </div>
      )}

      <div className={`flex-1 ${!hideTabs ? 'pt-6' : ''}`}>
        {activeTab === 'results' ? (
          analyzing ? (
            <div className="glass-card p-8 text-center">
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: '#6ee7ff' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Analyzing your performance with FSP Examiner AI...
              </p>
            </div>
          ) : analysis && transcript ? (
            <FeedbackDisplay 
              analysis={analysis}
              transcript={transcript}
            />
          ) : (
            <div className="glass-card p-8 text-center">
              <p style={{ color: 'var(--muted)' }}>
                Analysis failed or not available. Please try again.
              </p>
            </div>
          )
        ) : (
          <div className="glass-card p-8 md:p-12">
            {duration !== undefined && (
              <div className="mb-8 p-6 rounded-2xl" style={{ 
                background: 'rgba(110, 231, 255, 0.08)', 
                border: '1px solid rgba(110, 231, 255, 0.2)' 
              }}>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <p className="text-sm font-medium mb-1" style={{ color: 'var(--muted)' }}>
                      Session Duration
                    </p>
                    <p className="text-4xl font-semibold" style={{ 
                      background: 'var(--accent)', 
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text)' }}>
                Medical Case Details
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{ 
                  background: 'rgba(255, 255, 255, 0.04)' 
                }}>
                  <span className="text-sm font-medium" style={{ color: 'var(--muted)' }}>
                    Case ID:
                  </span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    {scenario.id}
                  </span>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl" style={{ 
                  background: 'rgba(255, 255, 255, 0.04)' 
                }}>
                  <span className="text-sm font-medium whitespace-nowrap" style={{ color: 'var(--muted)' }}>
                    Case Type:
                  </span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    {(() => {
                      const match = scenario.text.match(/\(([^)]+)\)/);
                      return match ? match[1] : 'Medical Case';
                    })()}
                  </span>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium mb-2" style={{ color: 'var(--muted)' }}>
                    Full Case Description:
                  </p>
                  <div className="max-h-96 overflow-y-auto p-4 rounded-xl" style={{ 
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {scenario.scientificScenario ? (
                      <div 
                        style={{
                          lineHeight: '1.6',
                          color: 'var(--text)'
                        }}
                      >
                        <style dangerouslySetInnerHTML={{ __html: `
                          .case-details strong { 
                            background: linear-gradient(135deg, #6ee7ff, #b892ff);
                            -webkit-background-clip: text;
                            -webkit-text-fill-color: transparent;
                            background-clip: text;
                            font-weight: 600; 
                            font-size: 0.95rem;
                          }
                          .case-details p { 
                            margin: 0.5rem 0; 
                          }
                          .case-details br { 
                            margin: 0.25rem 0; 
                          }
                          .case-details ul { 
                            margin: 0.5rem 0 1rem 1.5rem; 
                            list-style-type: disc;
                          }
                          .case-details li { 
                            margin: 0.25rem 0; 
                          }
                        `}} />
                        <div 
                          className="case-details"
                          dangerouslySetInnerHTML={{ __html: markdownToHtml(scenario.scientificScenario) }}
                        />
                      </div>
                    ) : (
                      <div className="text-sm" style={{ color: 'var(--text)' }}>
                        <div className="mb-4 p-3 rounded-lg" style={{ 
                          background: 'rgba(251, 191, 36, 0.1)',
                          border: '1px solid rgba(251, 191, 36, 0.3)'
                        }}>
                          <p className="text-xs" style={{ color: '#fbbf24' }}>
                            ℹ️ Original formatting (scientific scenario not available)
                          </p>
                        </div>
                        <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">
                          {scenario.text}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
